import { inject, injectable } from "inversify";
import {
  ICriarWebserviceModel,
  IControleDeChamadaPluggy,
  IResponseWebserviceModel
} from "../types";
import { WebserviceGateway } from "../ports/WebserviceGateway";
import {
  HttpStatusCode,
  IHttpResponse,
  type HttpClient
} from "@/core/services/protocols/types";
import { type IToastNotification } from "@/core/composables/notification/types";
import { DITypes } from "@/core/@types/types";

const mapControleDeChamada = (
  value: unknown
): IControleDeChamadaPluggy | undefined => {
  if (typeof value !== "object" || value === null) return undefined;

  const controle = value as Record<string, unknown>;

  return {
    IdentificadorDeAcesso:
      typeof controle.IdentificadorDeAcesso === "string"
        ? controle.IdentificadorDeAcesso
        : null,
    ItemAtualizando: Boolean(controle.ItemAtualizando),
    SincronizacaoPendente: Boolean(controle.SincronizacaoPendente)
  };
};

@injectable()
export default class WebserviceGatewayAdapter implements WebserviceGateway {
  @inject(DITypes.FINANCAS_API_HTTP_CLIENT)
  private httpClient!: HttpClient;

  @inject(DITypes.NOTIFICATION)
  private notification!: IToastNotification<string>;

  async criarWebservice(
    model: ICriarWebserviceModel,
    onErrorDisplayed: boolean,
    visibility: boolean = true,
    origem: string
  ): Promise<void> {
    console.log("Iniciando webservice: ", origem);

    return new Promise((resolve) => {
      this.httpClient
        ?.post<IHttpResponse>({
          url: "/v1/WebserviceAPI/Save",
          body: {
            ...model
          }
        })
        .then((response) => {
          if (!visibility) return;

          if (response?.statusCode != HttpStatusCode.success) {
            return this.notification.showNotification(
              "Houve um erro ao conectar a sua conta! Por favor, tente novamente mais tarde.",
              {
                type: "error",
                timeout: 3000
              }
            );
          } else if ((response?.body as any).Error !== undefined) {
            return this.notification.showNotification(
              (response?.body as any).Error,
              {
                type: "error",
                timeout: 10000
              }
            );
          }

          //__vmo.UI.AtivacaoAutomacaoBancaria.UpdateScreenState(id);//verificar o porque desse updateScreenState//implementar futuramente
          //função atualizar as grids e funcionalidades em tela

          if (onErrorDisplayed) return;

          this.notification.showNotification("Conexão Realizada com Sucesso!", {
            type: "success",
            timeout: 3000
          });

          setTimeout(() => {
            window.location.reload();
          }, 3000);
        })
        .catch(() => {
          this.notification.showNotification(
            "Houve um erro ao conectar a sua conta! Por favor, tente novamente mais tarde.",
            {
              type: "error",
              timeout: 3000
            }
          );
        })
        .finally(() => {
          resolve();
        });
    });
  }

  async editarWebservice(model: ICriarWebserviceModel): Promise<boolean> {
    const response = await this.httpClient?.post<IHttpResponse>({
      url: "/v1/WebserviceAPI/Edit",
      body: {
        ...model
      }
    });

    if (response?.statusCode != HttpStatusCode.success) {
      this.notification.showNotification(
        "Houve um erro ao conectar a sua conta! Por favor, tente novamente mais tarde.",
        {
          type: "error",
          timeout: 3000
        }
      );
    } else if ((response?.body as any).Error !== undefined) {
      this.notification.showNotification((response?.body as any).Error, {
        type: "error",
        timeout: 10000
      });
    }

    return response?.statusCode === HttpStatusCode.success &&
      (response?.body as any)?.Error === undefined;
  }

  async obterWebservicePluggyPorConta(
    bankAccount: string
  ): Promise<IResponseWebserviceModel> {
    const response = await this.httpClient?.get<IHttpResponse>({
      url: "/v1/WebserviceAPI/GetPluggyServiceByBankAccount",
      queryParams: { bankAccount }
    });

    const body = response.body as any;

    if (response?.statusCode != HttpStatusCode.success || body?.Error) {
      const model: IResponseWebserviceModel = {
        Ok: false,
        Error: body.Error,
        _id: "",
        API: "",
        ContaBancaria: null,
        Inativo: false,
        ApiPluggy: {
          itemId: "",
          WebserviceNotVisible: false,
          needConnectWidget: false,
          hasMFA: false,
          itemDeleted: false,
          connectionInvalid: false
        }
      };

      return model;
    }

    const model: IResponseWebserviceModel = {
      Ok: true,
      Error: undefined,
      _id: body.Result._id ?? null,
      API: body.Result.API ?? null,
      ContaBancaria: body.Result.ContaBancaria ?? null,
      Inativo: body.Result.Inativo ?? false,
      ApiPluggy: {
        itemId: body.Result.ApiPluggy?.itemId ?? "",
        accountId: body.Result.ApiPluggy?.accountId,
        lastSynchronization: body.Result.ApiPluggy?.lastSynchronization,
        lastUpdatedAt: body.Result.ApiPluggy?.lastUpdatedAt,
        hasMFA: body.Result.ApiPluggy?.hasMFA ?? false,
        WebserviceNotVisible:
          body.Result.ApiPluggy?.WebserviceNotVisible ?? false,
        needConnectWidget: body.Result.ApiPluggy?.needConnectWidget ?? false,
        itemDeleted: body.Result.ApiPluggy?.itemDeleted ?? false,
        connectionInvalid: body.Result.ApiPluggy?.connectionInvalid ?? false,
        executionStatus: body.Result.ApiPluggy?.executionStatus,
        ControleDeChamada: mapControleDeChamada(
          body.Result.ApiPluggy?.ControleDeChamada
        )
      }
    };

    return model;
  }

  async obterWebservicePluggy(
    id: string
  ): Promise<IResponseWebserviceModel | void> {
    const response = await this.httpClient?.get<IHttpResponse>({
      url: "/v1/WebserviceAPI/Get",
      queryParams: { id }
    });

    const body = response?.body as
      | {
          Ok?: boolean;
          Error?: string;
          Result?: Record<string, unknown>;
        }
      | undefined;
    const result = body?.Result;
    const apiPluggy = (result?.ApiPluggy ?? {}) as Record<string, unknown>;
    const contaBancaria = result?.ContaBancaria;

    if (response?.statusCode != HttpStatusCode.success || !body?.Ok || !result) {
      return {
        Ok: false,
        Error: body?.Error ?? "Não foi possível obter a integração Pluggy.",
        _id: id,
        API: "203",
        ContaBancaria: null,
        Inativo: false,
        ApiPluggy: {}
      };
    }

    return {
      Ok: true,
      Error: undefined,
      _id: String(result._id ?? id),
      API: String(result.API ?? "203"),
      ContaBancaria:
        typeof contaBancaria === "string" || typeof contaBancaria === "number"
          ? contaBancaria
          : typeof contaBancaria === "object" && contaBancaria !== null
            ? {
                _id: String(
                  (contaBancaria as { _id?: string | number })._id ?? ""
                )
              }
            : null,
      Inativo: Boolean(result.Inativo),
      ApiPluggy: {
        itemId:
          typeof apiPluggy.itemId === "string" ? apiPluggy.itemId : undefined,
        accountId:
          typeof apiPluggy.accountId === "string"
            ? apiPluggy.accountId
            : undefined,
        lastSynchronization:
          typeof apiPluggy.lastSynchronization === "string"
            ? apiPluggy.lastSynchronization
            : undefined,
        lastUpdatedAt:
          typeof apiPluggy.lastUpdatedAt === "string"
            ? apiPluggy.lastUpdatedAt
            : undefined,
        hasMFA: Boolean(apiPluggy.hasMFA),
        needConnectWidget: Boolean(apiPluggy.needConnectWidget),
        itemDeleted: Boolean(apiPluggy.itemDeleted),
        connectionInvalid: Boolean(apiPluggy.connectionInvalid),
        executionStatus:
          typeof apiPluggy.executionStatus === "string"
            ? apiPluggy.executionStatus
            : undefined,
        ControleDeChamada: mapControleDeChamada(
          apiPluggy.ControleDeChamada
        ),
        WebserviceNotVisible: Boolean(apiPluggy.WebserviceNotVisible)
      }
    };
  }
}
